/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";

const state = {
  // Quiz category data
  quizCategories: [],
  currentQuizCategory: null,

  // Loading states
  loadingQuizCategories: false,
  loadingCurrentQuizCategory: false,
  savingQuizCategory: false,
  importingQuizCategories: false,

  // Import progress
  importProgress: {
    total: 0,
    processed: 0,
    errors: [],
    isImporting: false
  },

  // Error state
  error: null
};

const actions = {
  /**
   * Load all quiz categories for the program
   */
  async loadQuizCategories({ commit, getters }) {
    commit("setLoadingQuizCategories", true);
    commit("setQuizCategoryError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizCategoriesRef = collection(programRef, "quizCategories");
      const quizCategoriesQuery = query(
        quizCategoriesRef,
        orderBy("title", "asc")
      );
      const querySnapshot = await getDocs(quizCategoriesQuery);

      const quizCategories = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        quizCategories.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setQuizCategories", quizCategories);
      return quizCategories;
    } catch (error) {
      console.error("Error loading quiz categories:", error);
      commit("setQuizCategoryError", error.message);
      commit("setQuizCategories", []);
      throw error;
    } finally {
      commit("setLoadingQuizCategories", false);
      commit("setLoadingTable", false); // For backward compatibility
    }
  },

  /**
   * Get quiz category by ID
   */
  async getQuizCategoryById({ commit, getters }, categoryId) {
    if (!categoryId) {
      console.error("Quiz category ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoryRef = doc(
        collection(programRef, "quizCategories"),
        categoryId
      );
      const categoryDoc = await getDoc(categoryRef);

      if (categoryDoc.exists()) {
        const data = categoryDoc.data();
        return {
          id: categoryDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Quiz category with ID ${categoryId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting quiz category by ID:", error);
      commit("setQuizCategoryError", error.message);
      return null;
    }
  },

  /**
   * Load current quiz category details
   */
  async loadCurrentQuizCategory({ commit, dispatch }, categoryId) {
    if (!categoryId) {
      throw new Error("Quiz category ID is required");
    }

    commit("setLoadingCurrentQuizCategory", true);
    commit("setCurrentQuizCategory", null);
    commit("setQuizCategoryError", null);

    try {
      const category = await dispatch("getQuizCategoryById", categoryId);
      if (category) {
        commit("setCurrentQuizCategory", category);
        return category;
      } else {
        throw new Error("Quiz category not found");
      }
    } catch (error) {
      console.error("Error loading current quiz category:", error);
      commit("setQuizCategoryError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentQuizCategory", false);
    }
  },

  /**
   * Create a new quiz category
   */
  async createQuizCategory({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Quiz category title is required");
    }

    commit("setSavingQuizCategory", true);
    commit("setQuizCategoryError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizCategoriesRef = collection(programRef, "quizCategories");

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles
      const duplicateQuery = query(
        quizCategoriesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("A quiz category with this title already exists");
      }

      const quizCategory = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system"
      };

      const newCategoryRef = await addDoc(quizCategoriesRef, quizCategory);

      const tempQuizCategory = {
        ...quizCategory,
        id: newCategoryRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createQuizCategory", tempQuizCategory);
      dispatch("setSnackbar", "Quiz category added successfully");

      return {
        success: true,
        categoryId: newCategoryRef.id,
        category: tempQuizCategory
      };
    } catch (error) {
      console.error("Error creating quiz category:", error);
      commit("setQuizCategoryError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when creating quiz category");
    } finally {
      commit("setSavingQuizCategory", false);
    }
  },

  /**
   * Update an existing quiz category
   */
  async updateQuizCategory({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id || !payload.title) {
      throw new Error("Quiz category ID and title are required");
    }

    commit("setSavingQuizCategory", true);
    commit("setQuizCategoryError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizCategoriesRef = collection(programRef, "quizCategories");
      const categoryRef = doc(quizCategoriesRef, payload.id);

      // Verify category exists
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        throw new Error("Quiz category not found");
      }

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles (excluding current category)
      const duplicateQuery = query(
        quizCategoriesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      let duplicated = false;
      duplicateSnapshot.forEach(docSnapshot => {
        if (docSnapshot.id !== payload.id) {
          duplicated = true;
        }
      });

      if (duplicated) {
        throw new Error("A quiz category with this title already exists");
      }

      const updateData = {
        ...payload,
        titleUppercase,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      await updateDoc(categoryRef, updateData);

      const tempQuizCategory = {
        ...categoryDoc.data(),
        ...payload,
        titleUppercase,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      commit("updateQuizCategory", tempQuizCategory);
      dispatch("setSnackbar", "Quiz category updated successfully");

      return tempQuizCategory;
    } catch (error) {
      console.error("Error updating quiz category:", error);
      commit("setQuizCategoryError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when updating quiz category");
    } finally {
      commit("setSavingQuizCategory", false);
    }
  },

  /**
   * Delete a quiz category
   */
  async deleteQuizCategory({ commit, getters, dispatch }, categoryId) {
    if (!categoryId) {
      throw new Error("Quiz category ID is required");
    }

    commit("setQuizCategoryError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoryRef = doc(
        collection(programRef, "quizCategories"),
        categoryId
      );

      await deleteDoc(categoryRef);

      commit("deleteQuizCategory", categoryId);
      dispatch("setSnackbar", "Quiz category deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting quiz category:", error);
      commit("setQuizCategoryError", error.message);
      throw new Error("Error when deleting quiz category");
    }
  },

  /**
   * Import multiple quiz categories with progress tracking
   */
  async importQuizCategories({ commit, getters, dispatch }, categoriesArray) {
    if (
      !categoriesArray ||
      !Array.isArray(categoriesArray) ||
      categoriesArray.length === 0
    ) {
      throw new Error("Categories array is required and must not be empty");
    }

    commit("setImportingQuizCategories", true);
    commit("setImportProgress", {
      total: categoriesArray.length,
      processed: 0,
      errors: [],
      isImporting: true
    });
    commit("setQuizCategoryError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizCategoriesRef = collection(programRef, "quizCategories");

      // Check for existing categories to avoid duplicates
      const existingQuery = query(quizCategoriesRef);
      const existingSnapshot = await getDocs(existingQuery);
      const existingTitles = new Set();
      existingSnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        if (data.titleUppercase) {
          existingTitles.add(data.titleUppercase);
        }
      });

      const batch = writeBatch(db());
      const errors = [];
      let processed = 0;
      const validCategories = [];

      categoriesArray.forEach((item, index) => {
        try {
          const title =
            typeof item === "string" ? item.trim() : item?.title?.trim();

          if (!title) {
            errors.push(`Row ${index + 1}: Empty title`);
            return;
          }

          const titleUppercase = title.toUpperCase();

          if (existingTitles.has(titleUppercase)) {
            errors.push(`Row ${index + 1}: "${title}" already exists`);
            return;
          }

          // Avoid duplicates within the import batch
          if (
            validCategories.some(cat => cat.titleUppercase === titleUppercase)
          ) {
            errors.push(`Row ${index + 1}: "${title}" is duplicated in import`);
            return;
          }

          const data = {
            title,
            titleUppercase,
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: getters.user?.id || "system",
            updatedBy: getters.user?.id || "system",
            description: typeof item === "object" ? item.description || "" : "",
            isActive: typeof item === "object" ? item.isActive !== false : true
          };

          const newCategoryRef = doc(quizCategoriesRef);
          batch.set(newCategoryRef, data);

          validCategories.push({
            ...data,
            id: newCategoryRef.id
          });

          existingTitles.add(titleUppercase);
          processed++;

          // Update progress
          commit("setImportProgress", {
            total: categoriesArray.length,
            processed,
            errors: [...errors],
            isImporting: true
          });
        } catch (error) {
          errors.push(`Row ${index + 1}: ${error.message}`);
        }
      });

      if (validCategories.length === 0) {
        throw new Error("No valid categories to import");
      }

      await batch.commit();

      // Add imported categories to state with proper timestamps
      const tempCategories = validCategories.map(cat => ({
        ...cat,
        created: new Date(),
        updated: new Date()
      }));

      commit("addQuizCategories", tempCategories);

      const successMessage = `Successfully imported ${processed} quiz categories`;
      if (errors.length > 0) {
        dispatch(
          "setSnackbar",
          `${successMessage}. ${errors.length} items had errors.`
        );
      } else {
        dispatch("setSnackbar", successMessage);
      }

      return {
        success: true,
        imported: processed,
        errors,
        total: categoriesArray.length
      };
    } catch (error) {
      console.error("Error importing quiz categories:", error);
      commit("setQuizCategoryError", error.message);
      throw new Error("Quiz categories import failed: " + error.message);
    } finally {
      commit("setImportingQuizCategories", false);
      commit("setImportProgress", {
        total: 0,
        processed: 0,
        errors: [],
        isImporting: false
      });
    }
  },

  /**
   * Duplicate a quiz category
   */
  async duplicateQuizCategory({ dispatch }, { categoryId, newTitle }) {
    if (!categoryId || !newTitle) {
      throw new Error("Category ID and new title are required");
    }

    try {
      const originalCategory = await dispatch(
        "getQuizCategoryById",
        categoryId
      );
      if (!originalCategory) {
        throw new Error("Original quiz category not found");
      }

      const duplicatedCategoryData = {
        ...originalCategory,
        title: newTitle,
        titleUppercase: newTitle.toUpperCase()
      };

      // Remove ID and timestamp fields
      delete duplicatedCategoryData.id;
      delete duplicatedCategoryData.created;
      delete duplicatedCategoryData.updated;

      const result = await dispatch(
        "createQuizCategory",
        duplicatedCategoryData
      );
      return result;
    } catch (error) {
      console.error("Error duplicating quiz category:", error);
      throw error;
    }
  },

  // State management actions
  clearQuizCategoryError({ commit }) {
    commit("setQuizCategoryError", null);
  },

  clearCurrentQuizCategory({ commit }) {
    commit("setCurrentQuizCategory", null);
  },

  resetImportProgress({ commit }) {
    commit("setImportProgress", {
      total: 0,
      processed: 0,
      errors: [],
      isImporting: false
    });
  }
};

const mutations = {
  setQuizCategories(state, payload) {
    state.quizCategories = payload;
  },

  setCurrentQuizCategory(state, payload) {
    state.currentQuizCategory = payload;
  },

  setLoadingQuizCategories(state, payload) {
    state.loadingQuizCategories = Boolean(payload);
  },

  setLoadingCurrentQuizCategory(state, payload) {
    state.loadingCurrentQuizCategory = Boolean(payload);
  },

  setSavingQuizCategory(state, payload) {
    state.savingQuizCategory = Boolean(payload);
  },

  setImportingQuizCategories(state, payload) {
    state.importingQuizCategories = Boolean(payload);
  },

  setImportProgress(state, payload) {
    state.importProgress = { ...payload };
  },

  setQuizCategoryError(state, payload) {
    state.error = payload;
  },

  createQuizCategory(state, payload) {
    state.quizCategories = [...state.quizCategories, payload];
  },

  updateQuizCategory(state, payload) {
    state.quizCategories = state.quizCategories.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current category if it matches
    if (
      state.currentQuizCategory &&
      state.currentQuizCategory.id === payload.id
    ) {
      state.currentQuizCategory = { ...state.currentQuizCategory, ...payload };
    }
  },

  deleteQuizCategory(state, categoryId) {
    state.quizCategories = state.quizCategories.filter(
      item => item.id !== categoryId
    );

    // Clear current category if it matches
    if (
      state.currentQuizCategory &&
      state.currentQuizCategory.id === categoryId
    ) {
      state.currentQuizCategory = null;
    }
  },

  addQuizCategories(state, categories) {
    state.quizCategories = [...state.quizCategories, ...categories];
  },

  // Backward compatibility
  setLoadingTable(state, payload) {
    state.loadingQuizCategories = Boolean(payload);
  }
};

const getters = {
  /**
   * Get all quiz categories
   */
  quizCategories(state) {
    return state.quizCategories;
  },

  /**
   * Get current quiz category
   */
  currentQuizCategory(state) {
    return state.currentQuizCategory;
  },

  /**
   * Get quiz category by ID
   */
  getQuizCategoryById: state => id => {
    return state.quizCategories.find(category => category.id === id) || null;
  },

  /**
   * Get quiz category by title
   */
  getQuizCategoryByTitle: state => title => {
    const titleUppercase = title.toUpperCase();
    return (
      state.quizCategories.find(
        category => category.titleUppercase === titleUppercase
      ) || null
    );
  },

  /**
   * Get quiz categories map (ID -> title)
   */
  quizCategoriesMap(state) {
    return state.quizCategories.reduce((result, item) => {
      return {
        ...result,
        [item.id]: item.title
      };
    }, {});
  },

  /**
   * Get active quiz categories
   */
  activeQuizCategories(state) {
    return state.quizCategories.filter(category => category.isActive !== false);
  },

  /**
   * Get quiz categories as options for dropdowns
   */
  quizCategoriesOptions(state) {
    return state.quizCategories.map(category => ({
      text: category.title,
      value: category.id
    }));
  },

  /**
   * Get active quiz categories as options
   */
  activeQuizCategoriesOptions(state) {
    return state.quizCategories
      .filter(category => category.isActive !== false)
      .map(category => ({
        text: category.title,
        value: category.id
      }));
  },

  /**
   * Get loading states
   */
  loadingQuizCategories(state) {
    return state.loadingQuizCategories;
  },

  loadingCurrentQuizCategory(state) {
    return state.loadingCurrentQuizCategory;
  },

  savingQuizCategory(state) {
    return state.savingQuizCategory;
  },

  importingQuizCategories(state) {
    return state.importingQuizCategories;
  },

  /**
   * Get import progress
   */
  quizCategoryImportProgress(state) {
    return state.importProgress;
  },

  /**
   * Get error state
   */
  quizCategoryError(state) {
    return state.error;
  },

  /**
   * Check if any quiz category operations are loading
   */
  quizCategoriesIsLoading(state) {
    return (
      state.loadingQuizCategories ||
      state.loadingCurrentQuizCategory ||
      state.savingQuizCategory ||
      state.importingQuizCategories
    );
  },

  /**
   * Get quiz categories count
   */
  quizCategoriesCount(state) {
    return state.quizCategories.length;
  },

  /**
   * Get active quiz categories count
   */
  activeQuizCategoriesCount(state) {
    return state.quizCategories.filter(category => category.isActive !== false)
      .length;
  },

  /**
   * Check if quiz categories exist
   */
  hasQuizCategories(state) {
    return state.quizCategories.length > 0;
  },

  /**
   * Check if active quiz categories exist
   */
  hasActiveQuizCategories(state) {
    return state.quizCategories.some(category => category.isActive !== false);
  },

  /**
   * Get quiz categories sorted by title
   */
  quizCategoriesSorted(state) {
    return [...state.quizCategories].sort((a, b) =>
      a.title.localeCompare(b.title)
    );
  },

  /**
   * Get recent quiz categories (last 10)
   */
  recentQuizCategories(state) {
    return [...state.quizCategories]
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 10);
  },

  /**
   * Search quiz categories by title
   */
  searchQuizCategories: state => searchTerm => {
    if (!searchTerm) return state.quizCategories;

    const term = searchTerm.toLowerCase();
    return state.quizCategories.filter(
      category =>
        category.title.toLowerCase().includes(term) ||
        (category.description &&
          category.description.toLowerCase().includes(term))
    );
  },

  /**
   * Get quiz category statistics
   */
  quizCategoryStatistics(state) {
    const total = state.quizCategories.length;
    const active = state.quizCategories.filter(cat => cat.isActive !== false)
      .length;
    const inactive = total - active;

    return {
      total,
      active,
      inactive
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
